import InstructorInfo from 'components/professors/InstructorInfo';
import { useEffect, useRef } from 'react';
import { Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
import { IProfessor } from 'interfaces/professor';
import { Text } from '@qcx/ui';
import { TitleMap } from 'services/professors';
import { useSelect } from '@refinedev/antd';
import { useCan } from '@refinedev/core';
import { InputMask } from 'components/form-items/InputMask';

export interface ProfessorInfoProps {
  professorData?: IProfessor;
  type?: 'show' | 'edit';
}

const ProfessorInfo = ({ professorData, type }: ProfessorInfoProps) => {
  const canListInstructors = useCan({ resource: 'instructors', action: 'list' });

  const { selectProps: instructorSelectProps } = useSelect({
    resource: 'instructors',
    queryOptions: {
      enabled: Boolean(type !== 'show' && canListInstructors.data?.can)
    },
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const stateId = Form.useWatch('state_id');
  const form = Form.useFormInstance();
  const prevStateIdRef = useRef<string | undefined>(undefined);

  const { selectProps: stateSelectProps } = useSelect({
    resource: 'states',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 30,
    pagination: { mode: 'server' }
  });

  const { selectProps: citiesSelectProps, queryResult: citiesQueryResult } =
    useSelect({
      resource: 'cities',
      optionLabel: 'name',
      optionValue: 'id',
      fetchSize: 999,
      filters: [
        {
          field: 'filter[by_state_id]',
          operator: 'eq',
          value: stateId
        }
      ],
      pagination: { mode: 'server' }
    });

  useEffect(() => {
    // Limpa a cidade somente quando o estado realmente muda após o primeiro render
    if (prevStateIdRef.current !== undefined && prevStateIdRef.current !== stateId) {
      form?.setFieldsValue({ city_id: null });
    }
    prevStateIdRef.current = stateId;
  }, [stateId, form]);

  const GenderMap = {
    male: 'Masculino',
    female: 'Feminino',
    non_binary: 'Não binário',
    prefer_not_to_say: 'Prefiro não informar'
  } as const;

  const EmploymentTypeMap = {
    pj: 'PJ',
    clt: 'CLT',
    rpa: 'RPA',
    hourly: 'Horista'
  } as const;

  return (
    <>
      <Typography.Title level={5} style={{ margin: '24px 0' }}>
        Dados Pessoais
      </Typography.Title>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true
              }
            ]}
          >
            {type !== 'show' ? <Input /> : <Text>{professorData?.name}</Text>}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="E-mail" name="email">
            {type !== 'show' ? <Input /> : <Text>{professorData?.email}</Text>}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="Telefone"
            name="phone_number"
            rules={[
              {
                pattern: /^\(\d{2}\) \d{5}-\d{4}$/,
                message: 'Número inválido'
              }
            ]}
          >
            {type !== 'show' ? (
              <InputMask mask="(00) 00000-0000" />
            ) : (
              <Text>{professorData?.phone_number}</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Data de nascimento" name="birthdate">
            {type !== 'show' ? (
              <Input type="date" />
            ) : (
              <Text>{professorData?.birthdate}</Text>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="Gênero"
            name="gender"
            getValueFromEvent={(value) => (value === undefined ? null : value)}
          >
            {type !== 'show' ? (
              <Select placeholder="Selecione um gênero..." allowClear>
                <Select.Option value="male">Masculino</Select.Option>
                <Select.Option value="female">Feminino</Select.Option>
                <Select.Option value="non_binary">Não binário</Select.Option>
                <Select.Option value="prefer_not_to_say">
                  Prefiro não informar
                </Select.Option>
              </Select>
            ) : (
              <Text>
                {professorData?.gender
                  ? GenderMap[professorData.gender as keyof typeof GenderMap]
                  : undefined}
              </Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Nacionalidade" name="nationality">
            {type !== 'show' ? <Input /> : <Text>{professorData?.nationality}</Text>}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="Estado"
            name="state_id"
            initialValue={professorData?.city?.state_id}
            getValueFromEvent={(value) => (value === undefined ? null : value)}
          >
            {type !== 'show' ? (
              <Select
                {...stateSelectProps}
                placeholder="Selecione um estado..."
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                allowClear
                onChange={(value) => {
                  if (!value) {
                    form?.setFieldsValue({ city_id: null });
                  }
                }}
              />
            ) : (
              <Text>
                {
                  stateSelectProps.options?.find(
                    (opt: any) => opt?.value === professorData?.city?.state_id
                  )?.label ?? professorData?.city?.state_id
                }
              </Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Cidade" name="city_id"
            initialValue={professorData?.city_id}
            getValueFromEvent={(value) => (value === undefined ? null : value)}
          >
            {type !== 'show' ? (
              <Select
                {...citiesSelectProps}
                options={stateId ? citiesSelectProps.options : []}
                loading={citiesQueryResult.fetchStatus === 'fetching'}
                disabled={!stateId}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Selecione uma cidade..."
                allowClear
              />
            ) : (
              <Text>{professorData?.city?.name}</Text>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="CPF/Documento"
            name="document_number"
            rules={[
              {
                pattern: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                message: 'CPF inválido'
              }
            ]}
          >
            {type !== 'show' ? (
              <InputMask mask="000.000.000-00" />
            ) : (
              <Text>{professorData?.document_number}</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="RG/Identidade" name="identity_number">
            {type !== 'show' ? <Input /> : <Text>{professorData?.identity_number}</Text>}
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Typography.Title level={5} style={{ margin: '12px 0' }}>
        Titulação acadêmica e Perfil profissional
      </Typography.Title>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label="Título"
            name="title"
            rules={[
              {
                required: true
              }
            ]}
          >
            {type !== 'show' ? (
              <Select
                placeholder="Selecione um titulo..."
                style={{ minWidth: 400 }}
              >
                <Select.Option value="specialist">Especialista</Select.Option>
                <Select.Option value="master">Mestre</Select.Option>
                <Select.Option value="doctor">Doutor</Select.Option>
              </Select>
            ) : (
              <Text>
                {TitleMap[professorData?.title as keyof typeof TitleMap]}
              </Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Vínculo empregatício"
            name="employment_type"
            getValueFromEvent={(value) => (value === undefined ? null : value)}
          >
            {type !== 'show' ? (
              <Select placeholder="Selecione um vínculo..." allowClear>
                <Select.Option value="pj">PJ</Select.Option>
                <Select.Option value="clt">CLT</Select.Option>
                <Select.Option value="rpa">RPA</Select.Option>
                <Select.Option value="hourly">Horista</Select.Option>
              </Select>
            ) : (
              <Text>
                {professorData?.employment_type
                  ? EmploymentTypeMap[professorData.employment_type as keyof typeof EmploymentTypeMap]
                  : undefined}
              </Text>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label="Lattes ID" name="lattes_id">
            {type !== 'show' ? (
              <Input placeholder="ID do Lattes" />
            ) : (
              <Text>{professorData?.lattes_id}</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Lattes (URL)" name="lattes_url">
            {type !== 'show' ? (
              <Input type="url" placeholder="https://lattes.cnpq.br/..." />
            ) : (
              <Text>{professorData?.lattes_url}</Text>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label="LinkedIn (URL)" name="linkedin_url">
            {type !== 'show' ? (
              <Input type="url" placeholder="https://www.linkedin.com/in/..." />
            ) : (
              <Text>{professorData?.linkedin_url}</Text>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Biografia" name="bio">
            {type !== 'show' ? (
              <Input.TextArea rows={4} />
            ) : (
              <Text>{professorData?.bio}</Text>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={[16, 16]} className="w-full">
        <Col className="flex justify-between">
          <Typography.Title level={5} style={{ margin: '12px 0' }}>
            Perfil na vitrine
          </Typography.Title>
        </Col>
      </Row>
      {type !== 'show' && (
        <Row gutter={[16, 16]} className="w-full">
          <Col className="flex justify-between w-full" md={6}>
            <Form.Item label="Associar perfil da vitrine" name="instructor_id">
              <Select
                {...instructorSelectProps}
                disabled={!canListInstructors.data?.can}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      {professorData && <InstructorInfo professorData={professorData} />}
    </>
  );
};

export default ProfessorInfo;
